import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useMemo, useCallback } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Dialog, DialogPortal } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { VideoCardUnified } from '@/components/shared/VideoCardUnified';
import { CompactFolderItem } from '@/components/video-editor/CompactFolderItem';
import { videoService } from '@/services/videoService';
import { hierarchicalFoldersService } from '@/services/hierarchicalFoldersService';
import { Video as VideoIcon, Search, Loader2, CheckCircle2, X, FolderOpen, Folders, ChevronRight, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { cn } from '@/lib/utils';

interface VideoItem {
  id: string;
  title: string;
  thumbnailUrl: string | null;
  duration: number;
  status?: string;
}

interface CurrentVideoInfo {
  id: string;
  title: string;
  thumbnailUrl: string;
}

interface ImportConfigurationsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentVideoId?: string;
  currentVideo?: CurrentVideoInfo | null;
  onSuccess?: () => void;
}

export const ImportConfigurationsModal: React.FC<ImportConfigurationsModalProps> = ({
  open,
  onOpenChange,
  currentVideoId,
  currentVideo,
  onSuccess,
}) => {
  useLanguage();
  const [selectedVideoIds, setSelectedVideoIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [isSelectingAll, setIsSelectingAll] = useState(false);

  // ── Queries ──────────────────────────────────────────────────────────────
  const { data: foldersData } = useQuery({
    queryKey: ['import-config-folders'],
    queryFn: () => hierarchicalFoldersService.listRoot(),
    enabled: open,
    staleTime: Infinity,
  });
  const rootFolders = foldersData?.items || [];

  const { data: folderDetails, isLoading: isLoadingFolder } = useQuery({
    queryKey: ['import-config-folder-details', currentFolderId],
    queryFn: () => hierarchicalFoldersService.getFolderDetails(currentFolderId!),
    enabled: !!currentFolderId && open,
    staleTime: Infinity,
  });

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingAllVideos,
  } = useInfiniteQuery({
    queryKey: ['videos-apply-config', 'all-including-folders'],
    queryFn: ({ pageParam = 1 }) =>
      videoService.getVideosList({
        page: pageParam,
        limit: 30,
        status: undefined,
        includeFolderVideos: true,
      }),
    getNextPageParam: (lastPage) =>
      lastPage.pagination.hasNextPage ? lastPage.pagination.page + 1 : undefined,
    enabled: open,
    initialPageParam: 1,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const allVideos: VideoItem[] = useMemo(() => {
    if (!data?.pages) return [];
    const query = searchQuery.trim().toLowerCase();
    return data.pages
      .flatMap(page => page.videos)
      .filter(v => ((v.status as string) === 'processed' || v.status === 'active') && v.id !== currentVideoId)
      .filter(v => !query || v.title.toLowerCase().includes(query))
      .map(v => ({
        id: v.id,
        title: v.title,
        thumbnailUrl: v.thumbnailUrl || null,
        duration: v.duration || 0,
        status: v.status,
      }));
  }, [data, currentVideoId, searchQuery]);

  const folderVideos: VideoItem[] = useMemo(() => {
    if (!folderDetails?.videos) return [];
    const query = searchQuery.trim().toLowerCase();
    return folderDetails.videos
      .filter(v => v.id !== currentVideoId)
      .filter(v => !query || v.title.toLowerCase().includes(query))
      .map(v => ({
        id: v.id,
        title: v.title,
        thumbnailUrl: v.thumbnailUrl || null,
        duration: v.duration || 0,
        status: v.status,
      }));
  }, [folderDetails, currentVideoId, searchQuery]);

  const currentVideos = currentFolderId ? folderVideos : allVideos;
  const isInitialLoading = !currentFolderId && isLoadingAllVideos;
  const selectionCount = selectedVideoIds.length;

  // ── Handlers ──────────────────────────────────────────────────────────────
  // Navega para qualquer pasta (raiz ou subpasta). O breadcrumb é montado a
  // partir de folderDetails.breadcrumb retornado pelo backend.
  const navigateToFolder = useCallback((folderId: string) => {
    setCurrentFolderId(folderId);
    setSearchQuery('');
  }, []);

  const navigateToRoot = useCallback(() => {
    setCurrentFolderId(null);
    setSearchQuery('');
  }, []);

  const toggleVideoSelection = useCallback((videoId: string) => {
    setSelectedVideoIds(prev =>
      prev.includes(videoId) ? prev.filter(id => id !== videoId) : [...prev, videoId]
    );
  }, []);

  const clearSelection = useCallback(() => setSelectedVideoIds([]), []);

  const handleSelectAll = useCallback(async () => {
    setIsSelectingAll(true);
    try {
      let allIds: string[] = [];

      if (currentFolderId) {
        // Dentro de uma pasta: seleciona apenas os vídeos da pasta atual.
        // folderVideos já está filtrado por searchQuery e exclui o currentVideo.
        allIds = folderVideos.map((v) => v.id);
      } else {
        // Em "Todos os vídeos": pagina todos respeitando a busca atual.
        const query = searchQuery.trim().toLowerCase();
        let page = 1;
        const limit = 500;
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const result = await videoService.getVideosList({
            page,
            limit,
            includeFolderVideos: true,
          });
          const pageIds = result.videos
            .filter(
              (v) =>
                ((v.status as string) === 'processed' || v.status === 'active') &&
                v.id !== currentVideoId,
            )
            .filter((v) => !query || v.title.toLowerCase().includes(query))
            .map((v) => v.id);
          allIds.push(...pageIds);
          if (!result.pagination.hasNextPage) break;
          page++;
        }
      }

      setSelectedVideoIds(allIds);
      if (allIds.length === 0) {
        toast.info(translateInterfaceText("Nenhum vídeo disponível para seleção"));
      } else {
        toast.success(
          `${allIds.length} ${allIds.length === 1 ? 'vídeo selecionado' : 'vídeos selecionados'}`,
        );
      }
    } catch (error) {
      console.error('Erro ao selecionar todos:', error);
      toast.error(translateInterfaceText("Erro ao selecionar todos os vídeos"));
    } finally {
      setIsSelectingAll(false);
    }
  }, [currentVideoId, currentFolderId, folderVideos, searchQuery]);

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (currentFolderId) return;
    const target = event.currentTarget;
    const pct = (target.scrollTop + target.clientHeight) / target.scrollHeight;
    if (pct > 0.8 && hasNextPage && !isFetchingNextPage) fetchNextPage();
  };

  const resetState = () => {
    setSelectedVideoIds([]);
    setSearchQuery('');
    setCurrentFolderId(null);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) resetState();
    onOpenChange(next);
  };

  const handleConfirm = async () => {
    if (!currentVideoId || selectedVideoIds.length === 0) return;
    setIsLoading(true);
    try {
      const result = await videoService.copyConfigurations(currentVideoId, selectedVideoIds);
      if (result.success) {
        toast.success(translateInterfaceText("Configurações aplicadas em {value0} vídeo(s)", { value0: (result.data?.updatedVideoIds.length || selectedVideoIds.length) }));
        onOpenChange(false);
        resetState();
        onSuccess?.();
      } else {
        toast.error(result.message || translateInterfaceText("Erro ao aplicar configurações"));
      }
    } catch (error: any) {
      console.error('Erro ao copiar configurações:', error);
      toast.error(error?.response?.data?.message || translateInterfaceText("Erro ao aplicar configurações"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogPortal>
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-[9998]',
            'bg-black/30 backdrop-blur-[6px]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0'
          )}
        />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className={cn(
            'fixed left-[50%] top-[50%] z-[9999] translate-x-[-50%] translate-y-[-50%]',
            'w-[95vw] sm:w-[80%] md:w-[65%] lg:w-[50%] max-w-[820px]',
            'h-[85vh] sm:h-[75vh] lg:h-[70vh]',
            'flex flex-col rounded-[20px] shadow-[0_12px_40px_rgba(0,0,0,0.18)] bg-white overflow-hidden',
            'focus:outline-none',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95'
          )}
        >
          <DialogPrimitive.Title className="sr-only">{translateInterfaceText("Aplicar Configurações")}</DialogPrimitive.Title>

          {/* Header */}
          <div className="px-4 md:px-7 pt-4 md:pt-5 pb-3 flex-shrink-0">
            <div className="flex items-start justify-between mb-1">
              <div>
                <h2 className="text-[16px] md:text-[18px] font-semibold text-[#111827]">
                  {translateInterfaceText("Aplicar Configurações")}</h2>
                <p className="text-[12px] md:text-[13px] text-[#6B7280] mt-1">
                  {translateInterfaceText("Selecione os vídeos que receberão as configurações do vídeo atual.")}</p>
              </div>
              <DialogPrimitive.Close asChild>
                <button
                  type="button"
                  className="w-8 h-8 rounded-full flex items-center justify-center text-[#9CA3AF] hover:bg-[#F3F4F6] hover:text-[#374151] transition-colors flex-shrink-0 ml-4"
                  aria-label={translateInterfaceText("Fechar")}
                >
                  <X className="w-4 h-4" />
                </button>
              </DialogPrimitive.Close>
            </div>

            {/* Vídeo de origem */}
            {currentVideoId && currentVideo && (
              <div className="mt-3">
                <p className="text-[11px] font-medium text-[#6B7280] uppercase tracking-wide mb-2">
                  {translateInterfaceText("Copiando de:")}</p>
                <div className="p-3 bg-[#EEF4FF] rounded-xl border border-[#C7D7FE]">
                  <div className="flex items-center gap-3">
                    <div className="w-16 h-9 rounded-lg overflow-hidden bg-[#E5E7EB] flex-shrink-0">
                      {currentVideo.thumbnailUrl ? (
                        <img src={currentVideo.thumbnailUrl} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <VideoIcon className="w-4 h-4 text-[#9CA3AF]" />
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[14px] font-medium text-[#1D4FFF] truncate">{currentVideo.title}</p>
                      <p className="text-[12px] text-[#6B7280]">{translateInterfaceText("Vídeo de origem")}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Body: sidebar + main */}
          <div className="flex flex-1 min-h-0 border-t border-[#F3F4F6]">

            {/* Sidebar — Pastas (oculta em mobile) */}
            <aside className="hidden md:block w-[200px] sm:w-[240px] border-r border-[#E5E7EB] flex-shrink-0 overflow-y-auto">
              <div className="py-4 space-y-1.5">
                <div
                  onClick={navigateToRoot}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigateToRoot(); }
                  }}
                  className={cn(
                    'group flex items-center gap-2.5 px-3 py-3 min-h-[48px] cursor-pointer transition-all duration-150 ease-out border-l-[3px]',
                    currentFolderId === null
                      ? 'border-l-[#2D6CF6]'
                      : 'border-l-transparent hover:text-[#2D6CF6]'
                  )}
                >
                  <Folders className={cn('w-5 h-5 flex-shrink-0 stroke-[1.5px]', currentFolderId === null ? 'text-[#2D6CF6]' : 'text-[#6B7280] group-hover:text-[#2D6CF6]')} />
                  <span className={cn('text-[14px] flex-1 truncate', currentFolderId === null ? 'text-[#2D6CF6] font-semibold' : 'text-[#111827] font-medium group-hover:text-[#2D6CF6]')}>
                    {translateInterfaceText("Todos os vídeos")}</span>
                </div>

                {rootFolders.map((folder) => (
                  <CompactFolderItem
                    key={folder.id}
                    id={folder.id}
                    name={folder.name}
                    videoCount={folder.videosCount || 0}
                    onClick={navigateToFolder}
                    isActive={currentFolderId === folder.id}
                  />
                ))}
              </div>
            </aside>

            {/* Main */}
            <div className="flex-1 min-w-0 min-h-0 flex flex-col">
              {/* Search + Selecionar todos */}
              <div className="px-5 pt-4 pb-3 flex-shrink-0">
                <div className="flex items-center gap-2">
                  <div className="relative flex-1 min-w-0">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                    <Input
                      type="text"
                      placeholder={translateInterfaceText("Pesquisar vídeos")}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 h-10 bg-white rounded-[12px] border border-[#E5E7EB] hover:border-[#D1D5DB] focus-visible:ring-2 focus-visible:ring-[#3B82F6] focus-visible:border-transparent transition-all text-[14px] placeholder:text-[#9CA3AF]"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleSelectAll}
                    disabled={isSelectingAll || isLoading}
                    className="h-10 px-3 text-[13px] font-medium rounded-[12px] border-[#D1D5DB] text-[#374151] hover:text-[#1D4FFF] hover:bg-[#EEF4FF] hover:border-[#C7D7FE] transition-colors whitespace-nowrap flex-shrink-0"
                  >
                    {isSelectingAll ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                        {translateInterfaceText("Selecionando...")}</>
                    ) : (
                      translateInterfaceText("Selecionar todos")
                    )}
                  </Button>
                </div>
              </div>

              {/* Banner de seleção */}
              {selectionCount > 0 && (
                <div className="px-5 pb-2 flex-shrink-0">
                  <div className="flex items-center justify-between px-4 py-2.5 rounded-[10px] bg-[#4B7BFF]/[0.06] border border-[#4B7BFF]/[0.15]">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-[#4B7BFF]" />
                      <span className="text-[13px] font-medium text-[#374151]">
                        {selectionCount} {selectionCount === 1 ? translateInterfaceText("vídeo selecionado") : translateInterfaceText("vídeos selecionados")}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={clearSelection}
                      className="flex items-center gap-1 text-[12px] text-[#6B7280] hover:text-[#374151] transition-colors cursor-pointer"
                    >
                      <X className="w-3.5 h-3.5" />
                      {translateInterfaceText("Desmarcar")}</button>
                  </div>
                </div>
              )}

              {/* Breadcrumb — montado a partir de folderDetails.breadcrumb (suporta subpastas) */}
              <div className="hidden md:block px-5 pb-2 flex-shrink-0">
                <nav className="flex items-center gap-1.5 text-[13px] flex-wrap">
                  {currentFolderId && folderDetails ? (
                    <>
                      <button
                        type="button"
                        onClick={navigateToRoot}
                        className="text-[#2D6CF6] hover:text-[#1d4ed8] font-medium transition-colors cursor-pointer"
                      >
                        {translateInterfaceText("Todos os vídeos")}</button>
                      {folderDetails.breadcrumb.map((item) => (
                        <React.Fragment key={item.id}>
                          <ChevronRight className="w-3.5 h-3.5 text-[#94A3B8]" />
                          <button
                            type="button"
                            onClick={() => navigateToFolder(item.id)}
                            className="text-[#2D6CF6] hover:text-[#1d4ed8] font-medium transition-colors cursor-pointer truncate max-w-[140px]"
                          >
                            {item.name}
                          </button>
                        </React.Fragment>
                      ))}
                      <ChevronRight className="w-3.5 h-3.5 text-[#94A3B8]" />
                      <span className="text-[#374151] font-semibold truncate max-w-[160px]">
                        {folderDetails.name}
                      </span>
                    </>
                  ) : (
                    <span className="text-[#374151] font-semibold">{translateInterfaceText("Todos os vídeos")}</span>
                  )}
                </nav>
              </div>

              {/* Grid de vídeos — área scrollável */}
              <div
                className="flex-1 min-h-0 overflow-y-auto px-5 pt-4 pb-5"
                onScroll={handleScroll}
              >
                {/* Subpastas — exibidas acima dos vídeos quando a pasta atual tem filhas */}
                {currentFolderId && !searchQuery.trim() && folderDetails?.children && folderDetails.children.length > 0 && (
                  <div className="hidden md:block mb-4">
                    <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wider mb-2">{translateInterfaceText("Subpastas")}</p>
                    <div className="grid grid-cols-2 gap-2">
                      {folderDetails.children.map(child => (
                        <button
                          key={child.id}
                          type="button"
                          onClick={() => navigateToFolder(child.id)}
                          className="flex items-center gap-2 px-3 py-2.5 rounded-[10px] border border-[#E5E7EB] hover:border-[#2D6CF6] hover:bg-[#EEF4FF] transition-colors text-left"
                        >
                          <Folders className="w-4 h-4 text-[#6B7280] flex-shrink-0" />
                          <span className="text-[14px] truncate flex-1 text-[#374151]">{child.name}</span>
                          <span className="text-[12px] text-[#9CA3AF] flex-shrink-0">{child.videosCount}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {isInitialLoading ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Loader2 className="w-8 h-8 text-[#2D6CF6] animate-spin mb-4" />
                    <p className="text-[13px] text-[#6B7280]">{translateInterfaceText("Carregando vídeos...")}</p>
                  </div>
                ) : currentFolderId && isLoadingFolder ? (
                  <div className="grid grid-cols-2 gap-3">
                    {[0, 1, 2, 3].map(i => (
                      <div key={i} className="h-[110px] rounded-xl bg-[#F3F4F6] animate-pulse" />
                    ))}
                  </div>
                ) : currentVideos.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <div className="w-12 h-12 rounded-full bg-[#F3F4F6] flex items-center justify-center mb-4">
                      {currentFolderId ? (
                        <FolderOpen className="w-6 h-6 text-[#9CA3AF]" />
                      ) : (
                        <VideoIcon className="w-6 h-6 text-[#9CA3AF]" />
                      )}
                    </div>
                    <p className="text-[15px] font-semibold text-[#111827] mb-1">
                      {currentFolderId
                        ? translateInterfaceText("Esta pasta não tem vídeos.")
                        : searchQuery
                          ? translateInterfaceText("Nenhum vídeo encontrado")
                          : translateInterfaceText("Nenhum vídeo disponível")}
                    </p>
                    <p className="text-[13px] text-[#6B7280]">
                      {searchQuery ? translateInterfaceText("Tente uma busca diferente.") : translateInterfaceText("Adicione vídeos para usar este recurso.")}
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Desktop: grid de cards */}
                    <div className="hidden md:grid grid-cols-2 gap-3">
                      {currentVideos.map((video) => (
                        <VideoCardUnified
                          key={video.id}
                          id={video.id}
                          title={video.title}
                          thumbnail={video.thumbnailUrl}
                          duration={video.duration}
                          variant="compact"
                          selected={selectedVideoIds.includes(video.id)}
                          isProcessing={video.status === 'processing'}
                          onSelect={() => toggleVideoSelection(video.id)}
                        />
                      ))}
                    </div>

                    {/* Mobile: lista compacta (mesmo padrão do modal do cloaker) */}
                    <div className="md:hidden border border-[#E5E7EB] rounded-[12px] overflow-hidden">
                      <div className="grid grid-cols-[80px_1fr] gap-3 px-3 py-2.5 bg-[#F9FAFB] border-b border-[#E5E7EB] font-medium text-[12px] text-[#6B7280]">
                        <div></div>
                        <div>{translateInterfaceText("Nome do Vídeo")}</div>
                      </div>
                      {currentVideos.map((video) => {
                        const isSelected = selectedVideoIds.includes(video.id);
                        return (
                          <div
                            key={video.id}
                            className={cn(
                              'grid grid-cols-[80px_1fr] gap-3 items-center p-2.5 transition-all duration-150 cursor-pointer relative border-b border-[#F3F4F6] last:border-b-0',
                              isSelected
                                ? 'bg-[#EBF5FF] border-l-[3px] border-l-[#1D4FFF]'
                                : 'bg-white hover:bg-[#f6f8ff] border-l-[3px] border-l-transparent'
                            )}
                            onClick={() => toggleVideoSelection(video.id)}
                          >
                            <div className="relative">
                              <img
                                src={video.thumbnailUrl || '/placeholder.svg'}
                                alt={video.title}
                                className="w-[72px] h-[40px] object-cover rounded-[6px] border border-[#E2E8F0] bg-[#F5F7FA]"
                              />
                              {isSelected && (
                                <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#1D4FFF] flex items-center justify-center shadow-sm">
                                  <Check className="w-2.5 h-2.5 text-white" />
                                </div>
                              )}
                            </div>
                            <div className="min-w-0">
                              <h4 className="font-medium text-[13px] truncate text-foreground">{video.title}</h4>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {isFetchingNextPage && !currentFolderId && (
                      <div className="flex justify-center py-4">
                        <Loader2 className="w-6 h-6 text-[#2D6CF6] animate-spin" />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-[#F3F4F6] px-7 py-4 flex items-center justify-end gap-3 flex-shrink-0 bg-white">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isLoading}
              className="border-[#D1D5DB] text-[#374151] hover:text-[#374151] hover:bg-[#F9FAFB] h-10 px-4 text-[13px] font-medium rounded-[10px]"
            >
              {translateInterfaceText("Cancelar")}</Button>
            <Button
              type="button"
              onClick={handleConfirm}
              disabled={selectionCount === 0 || isLoading}
              className="bg-[#2D6CF6] hover:bg-[#2154D6] text-white h-10 px-5 text-[13px] font-medium rounded-[10px] shadow-[0_2px_6px_rgba(45,108,246,0.25)] hover:shadow-[0_4px_12px_rgba(45,108,246,0.35)] transition-all duration-200 disabled:opacity-50 disabled:shadow-none"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {translateInterfaceText("Aplicando...")}</>
              ) : selectionCount > 0 ? (
                translateInterfaceText("Aplicar em {value0} {value1}", { value0: (selectionCount), value1: (selectionCount === 1 ? 'vídeo' : 'vídeos') })
              ) : (
                translateInterfaceText("Aplicar")
              )}
            </Button>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
};
